// OnlineFlowController — orchestrates the home-screen-resident party UI
// against a Colyseus PartyRoom. Subscribes to room.state.phase and
// mounts/unmounts the right sub-component for each phase. Routes Escape /
// B-button to ConfirmationPopup for the irreversible exit-online-play
// action.
//
// Responsibilities:
//   • PartyPanel mount/unmount + driving its members + joinLink + region
//   • MultiCharacterStage in sync with members
//   • SearchingModal up while phase === 'searching'
//   • TeamSelectScreen up while phase === 'team_select'
//   • Hand off to caller for 'vote' and 'launching' (those need the
//     existing MapVoteScreen + seat-reservation flow which lives in main.ts).
//   • Owner-initiated region migration: build a new client + room on the
//     target region, broadcast 'party:migrate' on the old room, surface
//     a 'regionSwitched' outcome to the caller so the outer party loop
//     can swap its client + room handles.
//   • Non-owner reaction to 'party:migrate' broadcast — surfaces a
//     'memberMigrate' outcome so the caller can reconnect on the new
//     region.
//
// Lifetime: the controller runs from "user picks ONLINE MATCH" until
// either match-launch (caller takes over) or user confirms leave online.

import { Client as ColyseusClient, type Room } from '@colyseus/sdk';
import { HomeScreen } from '../HomeScreen';
import { PartyPanel, type PartyPanelMember } from './PartyPanel';
import { showSearchingModal, type SearchingModalHandle } from './SearchingModal';
import { TeamSelectScreen, type TeamSelectMember } from './TeamSelectScreen';
import { showConfirmationPopup, type ConfirmationPopupHandle } from '../components/ConfirmationPopup';
import { showRegionSwitchModal, type RegionSwitchModalHandle } from './RegionSwitchModal';
import { MultiCharacterStage, type PartyMemberView } from '../../render/MultiCharacterStage';
import type { HatId } from '../../render/HatModels';
import { t } from '../../i18n';
import { getRegionById, REGIONS } from '../../net/regions';
import { savePreferredRegion } from '../regionSettings';

interface PartyMemberSchema {
  sessionId: string;
  name: string;
  color: string;
  hatId: string;
  level: number;
  isOwner: boolean;
  joinIndex: number;
  joinedAt: number;
  readyForTeamSelect: boolean;
  ping: number;
}

interface NpcPreviewSchema {
  charId: number;
  name: string;
  color: string;
  team: number; // 0 red, 1 blue
}

interface MatchHumanSchema {
  charId: number;
  sessionId: string;
  name: string;
  color: string;
  hatId: string;
  team: number; // 0 red, 1 blue
  isFromMyParty: boolean;
  isReady: boolean;
}

interface PartyStateSchema {
  phase: string;
  joinCode: string;
  members: ArrayLike<PartyMemberSchema> & Iterable<PartyMemberSchema>;
  humansFound: number;
  humansNeeded: number;
  searchSeconds: number;
  npcSlots: ArrayLike<NpcPreviewSchema> & Iterable<NpcPreviewSchema>;
  matchHumans: ArrayLike<MatchHumanSchema> & Iterable<MatchHumanSchema>;
  voteCountdown: number;
  teamSelectCountdown: number;
  mapVotes: { forEach(cb: (v: number, k: string) => void): void };
  mapId: string;
}

export type OnlinePhase =
  | 'party' | 'searching' | 'team_select' | 'vote' | 'launching';

export interface OnlineFlowCallbacks {
  /** Phase advanced to 'vote'. Caller mounts MapVoteScreen against this
   *  same room (the room continues hosting the vote). */
  onEnterVote(room: Room<PartyStateSchema>): void;
  /** Phase advanced to 'launching'. The 'go' message has already been or
   *  is about to be sent — caller takes over from here, including
   *  consuming the seat reservation and starting the match. */
  onLaunching(room: Room<PartyStateSchema>): void;
  /** A match group containing this party was aborted (e.g. another
   *  party's member disconnected mid-team-select). Caller can show a
   *  toast — the controller has already reverted the UI to 'party' phase. */
  onMatchAborted(reason: string): void;
  /** User confirmed leaving online play. Caller is responsible for
   *  leaving the room and tearing down the controller. */
  onLeaveOnline(): void;
  /** Owner finished bootstrapping the new region's party room. Caller
   *  takes over: leaves old room, swaps the active colyseus client +
   *  partyRoom, dismisses the in-flight migration modal, and continues
   *  the party loop with the new room. */
  onRegionSwitched(payload: {
    regionId: string;
    newClient: ColyseusClient;
    newRoom: Room<PartyStateSchema>;
    modal: RegionSwitchModalHandle;
  }): void;
  /** Member received a 'party:migrate' broadcast. Caller leaves the old
   *  room, builds a client on the new region, and joinById's the new
   *  PartyRoom. The controller has already shown a switch-modal which
   *  the caller dismisses on landing. */
  onMemberMigrate(payload: {
    regionId: string;
    roomId: string;
    modal: RegionSwitchModalHandle;
  }): void;
}

export class OnlineFlowController {
  private partyPanel: PartyPanel;
  private multiStage: MultiCharacterStage | null = null;
  private searchingModal: SearchingModalHandle | null = null;
  private teamSelectScreen: TeamSelectScreen | null = null;
  private confirmation: ConfirmationPopupHandle | null = null;
  private currentPhase: OnlinePhase = 'party';
  private disposed = false;
  private detachStateChange: (() => void) | null = null;
  private detachMessages: Array<() => void> = [];
  private detachFrameCallback: (() => void) | null = null;
  private escapeHandler: ((e: KeyboardEvent) => void) | null = null;
  private migrationInFlight = false;

  constructor(
    private readonly room: Room<PartyStateSchema>,
    private readonly homeScreen: HomeScreen,
    private readonly callbacks: OnlineFlowCallbacks,
    /** The region the party is currently connected to. Drives the panel's
     *  picker selected state. */
    private readonly currentRegionId: string,
    /** Identity (name, color, hatId, level) used when re-creating the
     *  party room on a different region during a switch. */
    private readonly identity: { name: string; color: string; hatId: string; level: number }
  ) {
    const stageHandle = homeScreen.getStageHandle();
    this.multiStage = new MultiCharacterStage(stageHandle);

    const previewEl = stageHandle.badgeRoot;
    this.partyPanel = new PartyPanel(this.findStageContainer(previewEl), {
      onCopyJoinLink: () => this.copyJoinLink(),
      onFindMatch: () => this.send('match:findMatch', {}),
      onLeaveParty: () => this.requestLeaveParty(),
      onSwitchRegion: (regionId) => this.requestSwitchRegion(regionId)
    });
  }

  start(): void {
    if (this.disposed) return;
    this.partyPanel.mount();
    this.partyPanel.setSelectedRegion(this.currentRegionId);
    this.partyPanel.setRegionLocked(this.computeRegionLocked());
    // Drive MultiCharacterStage's per-frame work from the home's rAF loop.
    this.detachFrameCallback = this.homeScreen.addFrameCallback((now, dt) => {
      this.multiStage?.tick(now, dt);
    });
    const initialState = this.room.state as PartyStateSchema;
    this.applyPhase((initialState.phase as OnlinePhase) || 'party');
    this.updateFromState();
    // Subscribe.
    const handler = (state: PartyStateSchema): void => {
      this.applyPhase(state.phase as OnlinePhase);
      this.updateFromState();
    };
    this.room.onStateChange(handler);
    this.detachStateChange = () => {
      // colyseus.js doesn't expose a removeListener for onStateChange,
      // re-register a no-op as the cheap workaround used elsewhere.
      try { this.room.onStateChange(() => { /* noop */ }); } catch { /* noop */ }
    };
    // Per-message handlers.
    const detachAbort = this.room.onMessage('match:aborted', (msg: { reason: string }) => {
      this.callbacks.onMatchAborted(msg.reason ?? 'unknown');
    });
    const detachMigrate = this.room.onMessage('party:migrate', (msg: { regionId: string; roomId: string }) => {
      this.handleIncomingMigrate(msg);
    });
    // Echo the server's 2s 'ping' as 'pong' so the server can compute
    // each client's RTT. Without this handler, the @colyseus/sdk emits
    // an "unhandled message" warning AND PartyMember.ping stays 0
    // forever for the local member.
    const detachPing = this.room.onMessage('ping', (msg: { t: number }) => {
      try { this.room.send('pong', { t: msg.t }); } catch { /* noop */ }
    });
    this.detachMessages.push(
      asDetach(detachAbort),
      asDetach(detachMigrate),
      asDetach(detachPing)
    );

    // Route escape to confirmation popup based on context.
    this.escapeHandler = (e: KeyboardEvent) => {
      if (this.disposed) return;
      if (e.code !== 'Escape' && e.code !== 'Backspace') return;
      // Don't intercept while searching modal has its own escape handler
      // (which routes to onCancel). We only handle escape on the
      // PartyPanel and TeamSelectScreen layers.
      if (this.currentPhase === 'searching') return;
      // Don't consume escape if a confirmation is already up.
      if (this.confirmation) return;
      e.preventDefault(); e.stopPropagation();
      this.openLeaveOnlineConfirmation();
    };
    addEventListener('keydown', this.escapeHandler, { capture: true });
  }

  async dispose(): Promise<void> {
    if (this.disposed) return;
    this.disposed = true;
    this.detachFrameCallback?.();
    this.detachFrameCallback = null;
    if (this.escapeHandler) {
      removeEventListener('keydown', this.escapeHandler, { capture: true } as EventListenerOptions);
      this.escapeHandler = null;
    }
    this.detachStateChange?.();
    this.detachStateChange = null;
    for (const fn of this.detachMessages) fn();
    this.detachMessages = [];
    this.searchingModal?.dismiss();
    this.searchingModal = null;
    this.confirmation?.dismiss();
    this.confirmation = null;
    if (this.teamSelectScreen) {
      await this.teamSelectScreen.leaveScreen();
      this.teamSelectScreen = null;
    }
    this.multiStage?.dispose();
    this.multiStage = null;
    await this.partyPanel.dispose();
    this.homeScreen.setPartyMode(1);
  }

  /** Drive per-frame updates of the multi-character stage. Called from
   *  HomeScreen's existing rAF loop via main.ts. */
  tick(nowMs: number, dt: number): void {
    this.multiStage?.tick(nowMs, dt);
  }

  /** Called by MapVoteScreen the moment the server's `go` arrives. The
   *  vote screen is about to morph + drain its loading bar over the
   *  in-flight wipe; during that window we don't want PartyPanel's MenuNav
   *  (B → leave-party) or our keyboard escape handler (Esc/Backspace →
   *  leave-online confirm) to fire over the loading screen. dispose() will
   *  null-check and skip the already-removed escape handler. */
  lockForLoading(): void {
    this.partyPanel.setEnabled(false);
    if (this.escapeHandler) {
      removeEventListener('keydown', this.escapeHandler, { capture: true } as EventListenerOptions);
      this.escapeHandler = null;
    }
  }

  // -- internals ----------------------------------------------------------

  private findStageContainer(_previewEl: HTMLElement): HTMLElement {
    // PartyPanel mounts inside the home's wide-stage so it inherits the
    // same scaling. We grab .wide-stage from the home root rather than
    // taking the preview's parent so the panel sits at consistent stage
    // coords regardless of how the preview is positioned.
    const home = document.getElementById('home-screen');
    if (!home) return document.body;
    const wide = home.querySelector('.wide-stage') as HTMLElement | null;
    return wide ?? home;
  }

  private applyPhase(phase: OnlinePhase): void {
    if (this.disposed) return;
    if (this.currentPhase === phase) return;
    const prev = this.currentPhase;
    this.currentPhase = phase;
    // Tear down outgoing artifacts.
    if (prev === 'searching' && this.searchingModal) {
      this.searchingModal.dismiss();
      this.searchingModal = null;
    }
    if (prev === 'team_select' && this.teamSelectScreen) {
      void this.teamSelectScreen.leaveScreen();
      this.teamSelectScreen = null;
    }
    // Set up incoming artifacts.
    if (phase === 'searching') {
      this.searchingModal = showSearchingModal({
        onCancel: () => this.send('match:cancel', {}),
        onJoinNpcsNow: () => {
          this.send('match:joinNpcsNow', {});
          this.searchingModal?.setWaitingForOthers(true);
        }
      }, {
        canRequestNpcFill: this.localIsOwner()
      });
      this.partyPanel.setEnabled(false);
    } else if (prev === 'searching') {
      this.partyPanel.setEnabled(true);
    }
    if (phase === 'team_select') {
      this.teamSelectScreen = new TeamSelectScreen();
      this.teamSelectScreen.mount({
        onSwapWithNpc: (npcCharId) => this.send('team:swap', { npcSlotIndex: npcCharId }),
        onReady: () => this.send('team:ready', { ready: true }),
        onRequestLeave: () => this.openLeaveOnlineConfirmation()
      });
      this.refreshTeamSelectFromState();
    }
    const overlayPhase = phase === 'team_select' || phase === 'vote' || phase === 'launching';
    const wasOverlay = prev === 'team_select' || prev === 'vote' || prev === 'launching';
    if (overlayPhase && !wasOverlay) this.homeScreen.root.style.zIndex = '5';
    if (!overlayPhase && wasOverlay) this.homeScreen.root.style.zIndex = '';
    if (phase === 'vote') {
      this.callbacks.onEnterVote(this.room);
    }
    if (phase === 'launching') {
      this.callbacks.onLaunching(this.room);
    }
  }

  private updateFromState(): void {
    const state = this.room.state as PartyStateSchema;
    const panelMembers: PartyPanelMember[] = [];
    const stageMembers: PartyMemberView[] = [];
    const membersIter = isIterable(state.members) ? state.members : null;
    const sortedMembers = membersIter
      ? Array.from(membersIter).sort((a, b) => a.joinIndex - b.joinIndex)
      : [];
    for (const m of sortedMembers) {
      const isLocal = m.sessionId === this.room.sessionId;
      panelMembers.push({
        sessionId: m.sessionId,
        name: m.name,
        color: m.color,
        hatId: (m.hatId || null) as HatId | null,
        level: m.level,
        isOwner: m.isOwner,
        isLocal,
        ping: m.ping || 0
      });
      stageMembers.push({
        sessionId: m.sessionId,
        name: m.name,
        color: m.color,
        hatId: (m.hatId || null) as HatId | null,
        level: m.level,
        joinIndex: m.joinIndex,
        isLocal,
        isOwner: m.isOwner
      });
    }
    this.partyPanel.setMembers(panelMembers);
    this.partyPanel.setJoinLink(this.buildJoinLink(state.joinCode || ''));
    this.partyPanel.setRegionLocked(this.computeRegionLocked());
    this.multiStage?.setMembers(stageMembers);
    this.homeScreen.setPartyMode(panelMembers.length);

    if (this.currentPhase === 'searching' && this.searchingModal) {
      this.searchingModal.setProgress(state.humansFound ?? 0, state.humansNeeded ?? 0);
      this.searchingModal.setSeconds(state.searchSeconds ?? 0);
      this.searchingModal.setCanRequestNpcFill(this.localIsOwner());
    }
    if (this.currentPhase === 'team_select' && this.teamSelectScreen) {
      this.refreshTeamSelectFromState();
      this.teamSelectScreen.setCountdown(state.teamSelectCountdown ?? 0);
    }
  }

  private refreshTeamSelectFromState(): void {
    if (!this.teamSelectScreen) return;
    const state = this.room.state as PartyStateSchema;
    const localSessionId = this.room.sessionId;
    const tsMembers: TeamSelectMember[] = [];
    if (isIterable(state.matchHumans)) {
      for (const h of state.matchHumans) {
        tsMembers.push({
          charId: h.charId,
          name: h.name,
          color: h.color,
          hatId: (h.hatId || null) as HatId | null,
          team: h.team === 0 ? 'red' : 'blue',
          isLocal: h.sessionId === localSessionId,
          isNpc: false,
          isReady: h.isReady,
          level: (h as unknown as { level?: number }).level ?? 0,
        });
      }
    }
    if (isIterable(state.npcSlots)) {
      for (const npc of state.npcSlots) {
        tsMembers.push({
          charId: npc.charId,
          name: npc.name,
          color: npc.color,
          hatId: null,
          team: npc.team === 0 ? 'red' : 'blue',
          isLocal: false,
          isNpc: true,
          isReady: false
        });
      }
    }
    tsMembers.sort((a, b) => a.charId - b.charId);
    const localCharId = tsMembers.find((m) => m.isLocal)?.charId ?? 0;
    this.teamSelectScreen.setMembers(tsMembers, localCharId);
  }

  private send(type: string, payload: unknown): void {
    try { this.room.send(type, payload); } catch (err) {
      console.warn(`[online] send(${type}) failed`, err);
    }
  }

  private localIsOwner(): boolean {
    const state = this.room.state as PartyStateSchema;
    if (!isIterable(state.members)) return false;
    for (const m of state.members) {
      if (m.sessionId === this.room.sessionId) return m.isOwner;
    }
    return false;
  }

  /** Region picker is interactive only when (a) more than one region
   *  exists in the live set AND (b) the local user is the party owner
   *  (which includes solo-of-1, since they're their own owner). The
   *  isGuestInThisRegion flag still matters for whether the caller
   *  persists the post-switch region as the user's preference, but the
   *  picker lock itself is purely owner-based: if everyone else leaves
   *  the autojoined party and the local user becomes the lone owner,
   *  they should be able to migrate back to their own region. */
  private computeRegionLocked(): boolean {
    if (REGIONS.length <= 1) return true;
    return !this.localIsOwner();
  }

  private buildJoinLink(joinCode: string): string {
    if (!joinCode) return '';
    if (typeof window === 'undefined' || !window.location) return '';
    const base = `${window.location.protocol}//${window.location.host}${window.location.pathname}`;
    return `${base}?join=${this.currentRegionId}-${encodeURIComponent(joinCode)}`;
  }

  private copyJoinLink(): void {
    const state = this.room.state as PartyStateSchema;
    const link = this.buildJoinLink(state.joinCode);
    if (!link) return;
    navigator.clipboard?.writeText(link).catch(() => { /* noop */ });
  }

  /** Owner-side region switch: build a new client + party room on the
   *  target region, broadcast 'party:migrate' to current members, surface
   *  the new room+client to the outer loop via `onRegionSwitched`. */
  private async requestSwitchRegion(regionId: string): Promise<void> {
    if (this.disposed || this.migrationInFlight) return;
    if (regionId === this.currentRegionId) return;
    if (!this.localIsOwner()) return;
    if (this.currentPhase !== 'party') return;
    const target = getRegionById(regionId);
    if (!target) return;

    this.migrationInFlight = true;
    const modal = showRegionSwitchModal(target.label);

    let newClient: ColyseusClient;
    let newRoom: Room<PartyStateSchema>;
    try {
      newClient = new ColyseusClient(target.url);
      const token = (() => {
        try { return localStorage.getItem('games:softAuthToken') ?? undefined; } catch { return undefined; }
      })();
      newRoom = await newClient.create<PartyStateSchema>('party', {
        name: this.identity.name,
        color: this.identity.color,
        hatId: this.identity.hatId,
        level: this.identity.level,
        ...(token ? { accountToken: token } : {}),
      });
    } catch (err) {
      console.error('[online] region switch — failed to create new party room', err);
      modal.dismiss();
      this.migrationInFlight = false;
      return;
    }

    // Broadcast the migration on the OLD room. Server validates owner-only
    // + phase==='party' and re-broadcasts to all members; the old room
    // disconnects 500ms later as a backstop.
    try {
      this.room.send('party:migrate', { regionId, roomId: newRoom.roomId });
    } catch (err) {
      console.warn('[online] party:migrate send failed', err);
    }

    // Persist the new region as the user's preferred. They explicitly
    // chose this — the migration succeeded as far as bootstrapping the
    // new room goes; member reconnection happens in parallel in the
    // outer loop.
    savePreferredRegion(regionId);

    this.callbacks.onRegionSwitched({ regionId, newClient, newRoom, modal });
    // The outer loop will dispose this controller; we stay in
    // migrationInFlight === true until then so concurrent picker clicks
    // are ignored.
  }

  /** Server broadcast a 'party:migrate' — we're a member following the
   *  owner to the new region. Show a switch modal and pass the room id
   *  up to the caller, which leaves the current room and joins the new
   *  one. */
  private handleIncomingMigrate(msg: { regionId: string; roomId: string }): void {
    if (this.disposed || this.migrationInFlight) return;
    // The owner triggers this too (their own party:migrate broadcasts
    // back), but they get the regionSwitched callback instead — so for
    // the owner, the migrationInFlight flag is already set above and
    // this early-return fires.
    this.migrationInFlight = true;
    const target = getRegionById(msg.regionId);
    const modal = showRegionSwitchModal(target?.label ?? msg.regionId);
    this.callbacks.onMemberMigrate({
      regionId: msg.regionId,
      roomId: msg.roomId,
      modal
    });
  }

  private requestLeaveParty(): void {
    if (this.confirmation) return;
    this.confirmation = showConfirmationPopup({
      title: t('confirm.leavePartyTitle'),
      body: t('confirm.leavePartyBody'),
      confirmLabel: t('confirm.leaveLabel'),
      destructive: true,
      onConfirm: () => {
        this.confirmation = null;
        this.callbacks.onLeaveOnline();
      },
      onCancel: () => { this.confirmation = null; }
    });
  }

  private openLeaveOnlineConfirmation(): void {
    if (this.confirmation) return;
    this.confirmation = showConfirmationPopup({
      title: t('confirm.leaveOnlineTitle'),
      confirmLabel: t('confirm.leaveLabel'),
      destructive: true,
      onConfirm: () => {
        this.confirmation = null;
        this.callbacks.onLeaveOnline();
      },
      onCancel: () => { this.confirmation = null; }
    });
  }
}

function asDetach(handler: unknown): () => void {
  // Colyseus' onMessage doesn't return a detach in 0.17 — we re-register
  // a no-op when needed in dispose(). Keep this signature so the call
  // site reads cleanly.
  void handler;
  return () => { /* no built-in detach */ };
}

/** Defensive iterability check for Colyseus schema fields that may not
 *  be decoded yet on the client at the moment of an initial-state read.
 *  ArraySchema / MapSchema instances expose Symbol.iterator once
 *  populated; before that they may be `undefined`. */
function isIterable<T>(v: unknown): v is Iterable<T> {
  return v != null && typeof (v as { [Symbol.iterator]?: unknown })[Symbol.iterator] === 'function';
}
